import { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import vehiclesData from "../../pages/trafficPage/data/vehicles.json";

// Requête GraphQL pour l'historique des trajets
const GET_TRAVEL_HISTORY = gql`
  query getTravelHistory(
    $uniqueId: String!
    $from: String!
    $to: String!
    $snapToRoad: Boolean
  ) {
    getTravelHistory(
      uniqueId: $uniqueId
      from: $from
      to: $to
      snapToRoad: $snapToRoad
    ) {
      distanceTravelledKms
      points {
        ts
        lat
        lng
        speed
        isHA
        isHB
        address
        __typename
      }
      __typename
    }
  }
`;

/**
 * Hook pour charger la liste des véhicules et leurs trajets
 * Utilise l'API GraphQL getTravelHistory pour fetcher les points GPS
 * @returns {Object} { vehicles, selectedVehicle, selectedTrip, tripData, loading, error, selectVehicle, selectTrip }
 */
export const useVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Hook Apollo pour appeler l'API de travel history
  const [getTravelHistory, { loading: tripLoading, error: tripError }] = useLazyQuery(GET_TRAVEL_HISTORY, {
    fetchPolicy: "network-only",
  });

  // Charger la liste des véhicules au montage
  useEffect(() => {
    try {
      // Charger les véhicules depuis le JSON
      setVehicles(vehiclesData.vehicles || []);
      setLoading(false);
    } catch (err) {
      console.error("Erreur lors du chargement des véhicules:", err);
      setLoading(false);
    }
  }, []);

  // Quand un trajet est sélectionné, fetcher les points GPS
  useEffect(() => {
    if (!selectedTrip || !selectedVehicle) {
      setTripData(null);
      return;
    }

    const fetchTripPoints = async () => {
      try {
        const { data } = await getTravelHistory({
          variables: {
            uniqueId: selectedVehicle.uniqueId,
            from: selectedTrip.from,
            to: selectedTrip.to,
            snapToRoad: false,
          },
        });

        if (data?.getTravelHistory) {
          setTripData({
            ...selectedTrip,
            ...data.getTravelHistory,
            vehicleName: selectedVehicle.vehicleNumber,
          });
        }
      } catch (err) {
        console.error("Erreur lors du chargement du trajet:", err);
        setTripData(null);
      }
    };

    fetchTripPoints();
  }, [selectedTrip, selectedVehicle, getTravelHistory]);

  // Sélectionner un véhicule
  const selectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Sélectionner automatiquement le premier trajet ou le trajet en cours
    const activeTrip = vehicle.trips.find(t => t.status === 'in_progress') || vehicle.trips[0];
    setSelectedTrip(activeTrip || null);
  };

  // Sélectionner un trajet spécifique
  const selectTrip = (trip) => {
    setSelectedTrip(trip);
  };

  // Réinitialiser la sélection
  const clearSelection = () => {
    setSelectedVehicle(null);
    setSelectedTrip(null);
    setTripData(null);
  };

  return {
    vehicles,
    selectedVehicle,
    selectedTrip,
    tripData,
    loading,
    tripLoading,
    tripError,
    selectVehicle,
    selectTrip,
    clearSelection,
  };
};
